// SPDX-License-Identifier: AGPL-3.0-only

import { requestJson } from "./http.js";

export interface ProbeHttpTargetConfiguration {
  name?: string;
  url: string;
  expectedStatus: number;
}

export interface ProbeConfiguration {
  probe: {
    cycleIntervalSeconds: number;
    timeoutSeconds: number;
    jitterPercent: number;
  };
  targets: {
    primary: ProbeHttpTargetConfiguration;
    secondary: ProbeHttpTargetConfiguration[];
  };
  upload: {
    intervalSeconds: number;
    batchSize: number;
    maxBatchBytes: number;
  };
}

export interface ProbeConfigurationBounds {
  readonly minProbeRequestDelaySeconds: number;
  readonly minProbeUploadIntervalSeconds: number;
}

export interface TenantProbeConfigurationState {
  readonly tenantId: string;
  readonly draft: ProbeConfiguration;
  readonly published: ProbeConfiguration | null;
  readonly publishedVersion: number | null;
  readonly bounds: ProbeConfigurationBounds;
  readonly updatedAt: string | null;
  readonly publishedAt: string | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readString(value: unknown, fieldName: string): string {
  if (typeof value !== "string") {
    throw new Error(`Probe configuration field ${fieldName} is invalid`);
  }
  return value;
}

function readNullableString(value: unknown, fieldName: string): string | null {
  if (value === null) {
    return null;
  }
  return readString(value, fieldName);
}

function readNumber(value: unknown, fieldName: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new Error(`Probe configuration field ${fieldName} is invalid`);
  }
  return value;
}

function readNullableNumber(value: unknown, fieldName: string): number | null {
  if (value === null) {
    return null;
  }
  return readNumber(value, fieldName);
}

function millisecondsToSeconds(value: number): number {
  return value / 1000;
}

function secondsToMilliseconds(value: number): number {
  return Math.round(value * 1000);
}

function readTarget(value: unknown, fieldName: string): ProbeHttpTargetConfiguration {
  if (!isRecord(value)) {
    throw new Error(`Probe configuration field ${fieldName} is invalid`);
  }
  const target: ProbeHttpTargetConfiguration = {
    url: readString(value.url, `${fieldName}.url`),
    expectedStatus: readNumber(value.expectedStatus, `${fieldName}.expectedStatus`),
  };
  if (value.name !== undefined) {
    return { ...target, name: readString(value.name, `${fieldName}.name`) };
  }
  return target;
}

function readConfiguration(value: unknown, fieldName: string): ProbeConfiguration {
  if (!isRecord(value) || !isRecord(value.probe) || !isRecord(value.targets) || !isRecord(value.upload)) {
    throw new Error(`Probe configuration field ${fieldName} is invalid`);
  }
  if (!Array.isArray(value.targets.secondary)) {
    throw new Error(`Probe configuration field ${fieldName}.targets.secondary is invalid`);
  }
  return {
    probe: {
      cycleIntervalSeconds: millisecondsToSeconds(readNumber(value.probe.cycleIntervalMs, `${fieldName}.probe.cycleIntervalMs`)),
      timeoutSeconds: millisecondsToSeconds(readNumber(value.probe.timeoutMs, `${fieldName}.probe.timeoutMs`)),
      jitterPercent: readNumber(value.probe.jitterPercent, `${fieldName}.probe.jitterPercent`),
    },
    targets: {
      primary: readTarget(value.targets.primary, `${fieldName}.targets.primary`),
      secondary: value.targets.secondary.map((target: unknown, index: number): ProbeHttpTargetConfiguration =>
        readTarget(target, `${fieldName}.targets.secondary[${index}]`),
      ),
    },
    upload: {
      intervalSeconds: millisecondsToSeconds(readNumber(value.upload.intervalMs, `${fieldName}.upload.intervalMs`)),
      batchSize: readNumber(value.upload.batchSize, `${fieldName}.upload.batchSize`),
      maxBatchBytes: readNumber(value.upload.maxBatchBytes, `${fieldName}.upload.maxBatchBytes`),
    },
  };
}

function readState(value: unknown): TenantProbeConfigurationState {
  if (!isRecord(value) || !isRecord(value.bounds)) {
    throw new Error("Probe configuration response is invalid");
  }
  return {
    tenantId: readString(value.tenantId, "tenantId"),
    draft: readConfiguration(value.draft, "draft"),
    published: value.published === null ? null : readConfiguration(value.published, "published"),
    publishedVersion: readNullableNumber(value.publishedVersion, "publishedVersion"),
    bounds: {
      minProbeRequestDelaySeconds: millisecondsToSeconds(readNumber(value.bounds.minProbeRequestDelayMs, "bounds.minProbeRequestDelayMs")),
      minProbeUploadIntervalSeconds: millisecondsToSeconds(readNumber(value.bounds.minProbeUploadIntervalMs, "bounds.minProbeUploadIntervalMs")),
    },
    updatedAt: readNullableString(value.updatedAt, "updatedAt"),
    publishedAt: readNullableString(value.publishedAt, "publishedAt"),
  };
}

function writeTarget(target: ProbeHttpTargetConfiguration): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    url: target.url,
    expectedStatus: target.expectedStatus,
  };
  if (target.name !== undefined) {
    payload.name = target.name;
  }
  return payload;
}

function writeConfiguration(configuration: ProbeConfiguration): Record<string, unknown> {
  return {
    probe: {
      cycleIntervalMs: secondsToMilliseconds(configuration.probe.cycleIntervalSeconds),
      timeoutMs: secondsToMilliseconds(configuration.probe.timeoutSeconds),
      jitterPercent: configuration.probe.jitterPercent,
    },
    targets: {
      primary: writeTarget(configuration.targets.primary),
      secondary: configuration.targets.secondary.map((target: ProbeHttpTargetConfiguration): Record<string, unknown> =>
        writeTarget(target),
      ),
    },
    upload: {
      intervalMs: secondsToMilliseconds(configuration.upload.intervalSeconds),
      batchSize: configuration.upload.batchSize,
      maxBatchBytes: configuration.upload.maxBatchBytes,
    },
  };
}

export async function loadTenantProbeConfiguration(tenantId: string): Promise<TenantProbeConfigurationState> {
  return readState(await requestJson(`/api/v1/tenants/${encodeURIComponent(tenantId)}/probe-configuration`));
}

export async function saveTenantProbeConfigurationDraft(
  tenantId: string,
  configuration: ProbeConfiguration,
): Promise<TenantProbeConfigurationState> {
  return readState(await requestJson(
    `/api/v1/tenants/${encodeURIComponent(tenantId)}/probe-configuration/draft`,
    {
      method: "PUT",
      body: JSON.stringify({ configuration: writeConfiguration(configuration) }),
    },
  ));
}

export async function publishTenantProbeConfiguration(tenantId: string): Promise<TenantProbeConfigurationState> {
  return readState(await requestJson(
    `/api/v1/tenants/${encodeURIComponent(tenantId)}/probe-configuration/publish`,
    { method: "POST" },
  ));
}
